import React from 'react'
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import { useMUITheme } from '../theme/ThemeProvider';

interface ConfirmModalProps {
    title: string
    content?: React.ReactNode
    cancelButtonText: string
    confirmButtonText: string
    onCancel: () => void
    onConfirm: () => void | Promise<void>
    open: boolean
    children?: React.ReactNode
    confirmDisabled?: boolean
    loading?: boolean
    size?: 'default' | 'large'
}

const DEFAULT_SIZE = { minWidth: 600, minHeight: 193 }
const LARGE_SIZE = { minWidth: 1150, minHeight: 600 }
const sizeMap = {
    default: DEFAULT_SIZE,
    large: LARGE_SIZE
} as const

export const ConfirmModal = ({
    title,
    content,
    cancelButtonText,
    confirmButtonText,
    onCancel,
    onConfirm,
    open,
    children,
    confirmDisabled,
    loading,
    size = 'default'
}: ConfirmModalProps) => {
    const { mode } = useMUITheme();
    const renderContent = () => {
        if (typeof content === 'string' || typeof content === 'number') {
            return <Typography>{content}</Typography>
        }
        return content
    }

    return (
        <Dialog
            open={open}
            data-testid="confirm-modal"
            slotProps={{
                paper: {
                    sx: {
                        minWidth: sizeMap[size].minWidth,
                        minHeight: sizeMap[size].minHeight,
                        bgcolor: 'background.default'
                    }
                }
            }}
        >
            <DialogTitle data-testid="confirm-modal-title">{title}</DialogTitle>
            <DialogContent sx={{ height: "100%", paddingRight: '2.5rem' }} data-testid="confirm-modal-content">
                {!!content ? renderContent() : null}
                {children}
            </DialogContent>
            <DialogActions sx={{ p: '1rem 1.5rem', gap: '0.5rem' }}>
                <Button onClick={onCancel} variant="outlined" sx={{ color: mode === 'dark' ? 'text.primary' : '#000' }} data-testid={`${cancelButtonText.toLowerCase().replace(/ /g, '-')}-button`}>
                    {cancelButtonText}
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    color='secondary'
                    disabled={Boolean(confirmDisabled || loading)}
                    sx={{ width: 'auto' }}
                    data-testid={`${confirmButtonText.toLowerCase().replace(/ /g, '-')}-button`}
                    aria-label={confirmButtonText}
                >
                    {loading ? <CircularProgress size='1.5rem' sx={{ color: '#fff' }} /> : confirmButtonText}
                </Button>
            </DialogActions>
        </Dialog>
    )
}